import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Plus,
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Package, 
  Euro, 
  Users, 
  Calendar, 
  Search, 
  Filter,
  Image as ImageIcon,
  CheckCircle,
  Ban,
  Star,
  Tag,
  Clock,
  AlertCircle,
  Leaf
} from 'lucide-react';
import apiService from '../services/api';
import ProduitModal from '../components/ProduitModal';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';
import { resolveMerchantOperationalScope } from '../utils/assistanceSession';

const Produits = () => {
  const [items, setItems] = useState([]);
  const [commercants, setCommercants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // 50 produits par page pour de meilleures performances
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // Récupérer l'utilisateur actuel (utilise apiService pour plus de fiabilité)
  const getCurrentUser = () => {
    try {
      // Essayer d'abord avec apiService.getStoredUser() (plus fiable)
      const storedUser = apiService.getStoredUser();
      if (storedUser) {
        return storedUser;
      }
      // Fallback vers localStorage direct (legacy)
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  // 🔧 CORRECTION - Initialiser selectedCommercant correctement dès le début
  // Si on est un commerçant, initialiser avec son commercantId au lieu de 'all'
  const getInitialSelectedCommercant = () => {
    const scope = resolveMerchantOperationalScope(getCurrentUser());
    return scope.scopedToMerchant && scope.commercantId ? scope.commercantId : 'all';
  };
  
  const [selectedCommercant, setSelectedCommercant] = useState(getInitialSelectedCommercant);
  const [searchCommercant, setSearchCommercant] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  // Recherche et filtres pour les produits
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('nom');

  // Ref pour annuler les requêtes en cours
  const abortControllerRef = useRef(null);
  
  const fetchData = useCallback(async () => {
      // Annuler les requêtes précédentes si elles existent
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      try {
        setLoading(true);
        setError('');
        
        // Timeout global de 15 secondes pour éviter les blocages
        const timeoutId = setTimeout(() => {
          if (!signal.aborted) {
            abortControllerRef.current?.abort();
            setError('Le chargement prend trop de temps. Veuillez actualiser la page.');
            setLoading(false);
          }
        }, 15000);
        
        // Récupérer l'utilisateur actuel + portée assistance
        const currentUser = getCurrentUser();
        const scope = resolveMerchantOperationalScope(currentUser);
        const isCommercant = scope.scopedToMerchant;
        const userCommercantId = scope.commercantId;
        
        // 🔧 RÉCUPÉRATION BACKEND ROBUSTE - Si COMMERCANT sans commercantId, récupérer depuis le backend avec retry
        let finalUser = currentUser;
        let finalCommercantId = userCommercantId;
        
        if (isCommercant && !userCommercantId && !signal.aborted) {
          // Retry optimisé : max 2 tentatives avec délais réduits et timeout
          const maxRetries = 2;
          let retryCount = 0;
          let success = false;
          
          while (!success && retryCount < maxRetries && !signal.aborted) {
            try {
              const backendUser = await Promise.race([
                apiService.verifyToken(),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Timeout')), 2000)
                )
              ]);
              
              const userData = backendUser?.user || backendUser?.data || backendUser;
              
              if (userData) {
                const backendCommercantId = userData?.commercantId 
                  || userData?.commercant?.id 
                  || (userData?.commercant && typeof userData.commercant === 'string' ? userData.commercant : null)
                  || null;
                
                if (backendCommercantId) {
                  localStorage.setItem('admin_user', JSON.stringify(userData));
                  finalUser = userData;
                  finalCommercantId = backendCommercantId;
                  success = true;
                  console.log('[Produits] ✅ commercantId récupéré après', retryCount + 1, 'tentative(s)');
                }
              }
            } catch (err) {
              if (signal.aborted) break;
              retryCount++;
              if (retryCount < maxRetries) {
                // Délai réduit : 300ms puis 600ms
                await new Promise(resolve => setTimeout(resolve, 300 * retryCount));
              }
            }
          }
        }
        
        // 🔒 PHASE 2 - Sécurisation : COMMERCANT sans commercantId = état invalide
        // Blocage explicite, aucun fallback "all products"
        // MAIS seulement après avoir épuisé toutes les tentatives
        if (isCommercant && !finalCommercantId) {
          console.error('[Produits] ❌ Impossible de récupérer commercantId après toutes les tentatives');
          setError('Impossible de récupérer votre boutique. Veuillez contacter le support.');
          setItems([]);
          setLoading(false);
          return; // Ne pas continuer, aucun chargement
        }
        
        let allProduits = [];
        let allPrestations = [];

        if (isCommercant && finalCommercantId) {
          // Pour un commerçant, charger uniquement SES produits avec pagination
          try {
            // Paralléliser les appels API pour améliorer les performances
            const [produitsData, prestationsData, commercant] = await Promise.allSettled([
              apiService.getProduitsByCommercant(finalCommercantId, true, { page: currentPage, pageSize }),
              apiService.getPrestations(`?commercantId=${finalCommercantId}`),
              apiService.getCommercant(finalCommercantId)
            ]);
            
            // Traiter les résultats avec pagination
            if (produitsData.status === 'fulfilled') {
              const result = produitsData.value;
              // Nouveau format avec pagination
              if (result.produits && result.total !== undefined) {
                allProduits = Array.isArray(result.produits) ? result.produits : [];
                setTotalItems(result.total || 0);
                setTotalPages(result.pages || 1);
              } else {
                // Ancien format (compatibilité)
                allProduits = Array.isArray(result?.produits) ? result.produits : (Array.isArray(result) ? result : []);
                setTotalItems(allProduits.length);
                setTotalPages(1);
              }
            }
            
            if (prestationsData.status === 'fulfilled') {
              const prestations = prestationsData.value;
              allPrestations = Array.isArray(prestations?.prestations) 
                ? prestations.prestations.filter(p => p.commercantId === finalCommercantId)
                : Array.isArray(prestations)
                ? prestations.filter(p => p.commercantId === finalCommercantId)
                : [];
            }
            
            if (commercant.status === 'fulfilled') {
              setCommercants(commercant.value ? [commercant.value] : []);
            } else {
              setCommercants([]);
            }
          } catch (err) {
            console.error('[Produits] Erreur chargement produits commerçant:', err);
            setError('Erreur lors du chargement des produits');
          }
        } else {
          // Pour le super admin, récupérer TOUS les produits (actifs et inactifs)
          // Le backend limite à 100 par page, donc on fait plusieurs appels si nécessaire
          // Limiter à 10 pages max pour éviter les blocages (1000 produits max)
          let page = 1;
          let hasMore = true;
          const pageSize = 100;
          const maxPages = 10; // Limite de sécurité
          
          while (hasMore && page <= maxPages && !signal.aborted) {
            try {
              const produitsData = await Promise.race([
                apiService.getProduits(`?includeInactifs=true&page=${page}&pageSize=${pageSize}`),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Timeout produits')), 5000)
                )
              ]);
              
              const produitsPage = Array.isArray(produitsData?.produits)
                ? produitsData.produits
                : Array.isArray(produitsData) 
                ? produitsData
                : [];
              
              allProduits = [...allProduits, ...produitsPage];
              
              const total = produitsData?.total || 0;
              hasMore = allProduits.length < total && produitsPage.length === pageSize;
              page++;
            } catch (err) {
              if (signal.aborted) break;
              console.warn(`[Produits] Erreur page ${page}:`, err.message);
              hasMore = false; // Arrêter en cas d'erreur
            }
          }

          // Récupérer les prestations (même logique avec limite)
          if (!signal.aborted) {
            page = 1;
            hasMore = true;
            
            while (hasMore && page <= maxPages && !signal.aborted) {
              try {
                const prestationsData = await Promise.race([
                  apiService.getPrestations(`?page=${page}&pageSize=${pageSize}`),
                  new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Timeout prestations')), 5000)
                  )
                ]);
                
                const prestationsPage = Array.isArray(prestationsData?.prestations)
                  ? prestationsData.prestations
                  : Array.isArray(prestationsData)
                  ? prestationsData
                  : [];
                
                allPrestations = [...allPrestations, ...prestationsPage];
                
                const total = prestationsData?.total || 0;
                hasMore = allPrestations.length < total && prestationsPage.length === pageSize;
                page++;
              } catch (err) {
                if (signal.aborted) break;
                console.warn(`[Produits] Erreur prestations page ${page}:`, err.message);
                hasMore = false;
              }
            }
          }

          // Récupérer les commerçants
          if (!signal.aborted) {
            try {
              const commercantsData = await Promise.race([
                apiService.getCommercants(),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Timeout commerçants')), 5000)
                )
              ]);
              
              // Gérer différentes structures de réponse
              const commercantsArray = Array.isArray(commercantsData) 
                ? commercantsData 
                : (commercantsData?.data || commercantsData?.commercants || []);
              
              setCommercants(commercantsArray);
            } catch (err) {
              if (!signal.aborted) {
                console.error('[Produits] Erreur chargement commerçants:', err);
                setCommercants([]);
              }
            }
          }
        }

        if (signal.aborted) {
          return; // Ne pas mettre à jour l'état si annulé
        }

        clearTimeout(timeoutId);
        
        const produitsList = allProduits.map((produit) => ({ ...produit, type: 'produit' }));
        const prestationsList = allPrestations.map((prestation) => ({ ...prestation, type: 'prestation' }));
        
        const finalItems = [...produitsList, ...prestationsList];
        setItems(finalItems);
      } catch (err) {
        if (!abortControllerRef.current?.signal.aborted) {
          setError('Erreur lors du chargement des données');
          console.error('Produits error:', err);
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setLoading(false);
        }
      }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchData();
    
    // Cleanup : annuler les requêtes si le composant est démonté
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Extraire les catégories des produits
  useEffect(() => {
    if (items.length > 0) {
      const cats = Array.from(
        new Set(
          items
            .filter(item => item.type === 'produit' && item.categorie)
            .map(item => item.categorie)
            .filter(c => typeof c === 'string' && c.trim().length > 0)
        )
      ).sort();
      setCategories(cats);
    }
  }, [items]);

  const getCommercantName = (commercantId) => {
    if (!commercantId) return 'Sans commerçant';
    const commercant = commercants.find(c => c.id === commercantId);
    return commercant ? commercant.nom : 'Commerçant inconnu';
  };

  // Filtre les commerçants pour la recherche
  const filteredCommercants = useMemo(() => {
    if (!searchCommercant) return commercants;
    const searchLower = searchCommercant.toLowerCase();
    return commercants.filter(c => 
      c.nom?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower)
    );
  }, [commercants, searchCommercant]);

  const handleToggleActif = async (e, item) => {
    e.stopPropagation();
    const newActifStatus = !item.actif;
    
    try {
      // Optimistic update : mettre à jour l'état immédiatement
      setItems(prevItems => 
        prevItems.map(i => i.id === item.id ? { ...i, actif: newActifStatus } : i)
      );
      
      // Appel API
      if (item.type === 'produit') {
        await apiService.updateProduit(item.id, { actif: newActifStatus });
      } else {
        // Pour les prestations, utiliser l'endpoint approprié si disponible
        await apiService.updatePrestation(item.id, { actif: newActifStatus });
      }
      
      // Mise à jour finale avec la réponse du serveur (si disponible)
      // L'optimistic update devrait déjà avoir mis à jour l'UI
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      // Rollback en cas d'erreur
      setItems(prevItems => 
        prevItems.map(i => i.id === item.id ? { ...i, actif: item.actif } : i)
      );
      alert('Erreur lors du changement de statut');
    }
  };

  const handleEdit = async (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Ne fonctionne que pour les produits, pas les prestations
    if (item.type !== 'produit') {
      alert('L\'édition n\'est disponible que pour les produits');
      return;
    }
    
    try {
      // Charger le produit complet depuis l'API
      const response = await apiService.getProduit(item.id);
      const produit = response?.produit || response;
      if (produit) {
        setSelectedProduit(produit);
        setShowModal(true);
      } else {
        alert('Produit introuvable');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du produit:', err);
      alert('Erreur lors du chargement du produit');
    }
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${item.nom}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      setDeletingId(item.id);
      if (item.type === 'produit') {
        await apiService.deleteProduit(item.id);
      } else {
        // Pour les prestations, utiliser l'endpoint approprié si disponible
        await apiService.deletePrestation(item.id);
      }
      // Retirer l'élément de la liste
      setItems(items.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // Filtrage et tri des items
  const filteredItems = useMemo(() => {
    let result = items;
    
    // Filtre par commerçant (pour SUPER_ADMIN)
    if (selectedCommercant !== 'all') {
      result = result.filter((item) => {
        // Le commercantId est toujours présent directement sur le produit
        // L'objet commercant n'a que { nom: true } dans les listes, pas d'id
        const itemCommercantId = item.commercantId;
        if (!itemCommercantId) {
          return false;
        }
        // Normaliser la comparaison (string vs number)
        return String(itemCommercantId) === String(selectedCommercant);
      });
    }
    
    // Recherche par nom
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => 
        item.nom?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.categorie?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtre par statut
    if (filterStatus !== 'all') {
      result = result.filter((item) => 
        filterStatus === 'actif' ? item.actif : !item.actif
      );
    }
    
    // Tri
    result = [...result].sort((a, b) => {
      if (sortBy === 'nom') {
        return (a.nom || '').localeCompare(b.nom || '');
      } else if (sortBy === 'prix') {
        return (a.prix || 0) - (b.prix || 0);
      } else if (sortBy === 'categorie') {
        return (a.categorie || '').localeCompare(b.categorie || '');
      }
      return 0;
    });
    
    return result;
  }, [items, selectedCommercant, searchTerm, filterStatus, sortBy]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
            <p className="text-gray-600">Gestion des produits de la plateforme</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const handleAddProduit = () => {
    setSelectedProduit(null);
    setShowModal(true);
  };

  // Récupérer le commercantId pour l'ajout de produit
  const getCommercantIdForNewProduit = () => {
    const scope = resolveMerchantOperationalScope(getCurrentUser());
    const isCommercant = scope.scopedToMerchant;
    const userCommercantId = scope.commercantId;
    
    if (isCommercant && userCommercantId) {
      return userCommercantId;
    }
    
    // Si super admin avec un commerçant sélectionné, utiliser celui-ci
    if (selectedCommercant && selectedCommercant !== 'all') {
      return selectedCommercant;
    }
    
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
          <p className="text-gray-600">Récapitulatif global des produits physiques et prestations de la plateforme</p>
        </div>
        {getCommercantIdForNewProduit() && (
          <button
            onClick={handleAddProduit}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter un produit</span>
          </button>
        )}
      </div>

      {/* Filtres et recherche */}
      <div className="space-y-4">
        {/* Recherche et filtres produits */}
        <div className="card">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Tous</option>
                <option value="actif">Actifs</option>
                <option value="inactif">Inactifs</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="nom">Tri par nom</option>
                <option value="prix">Tri par prix</option>
                <option value="categorie">Tri par catégorie</option>
              </select>
            </div>
          </div>
        </div>

        {/* Filtre par commerçant avec recherche - Masqué pour les commerçants */}
        {(() => {
          const isCommercant = resolveMerchantOperationalScope(getCurrentUser()).scopedToMerchant;
          
          if (isCommercant) {
            // Pour un commerçant / assistance, ne pas afficher le filtre
            return null;
          }
          
          return (
            <div className="card">
              <div className="flex items-center space-x-4 flex-wrap gap-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Filtrer par commerçant:</label>
                <div className="flex-1 min-w-0 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un commerçant..."
                      value={searchCommercant}
                      onChange={(e) => setSearchCommercant(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <select
                  value={selectedCommercant}
                  onChange={(e) => {
                    setSelectedCommercant(e.target.value);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[200px]"
                >
                  <option value="all">Tous les commerçants</option>
                  {commercants.length === 0 ? (
                    <option value="" disabled>Chargement des commerçants...</option>
                  ) : filteredCommercants.length > 0 ? (
                    filteredCommercants.map(commercant => (
                      <option key={commercant.id} value={commercant.id}>
                        {commercant.nom}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Aucun commerçant trouvé</option>
                  )}
                </select>
                {searchCommercant.trim() !== '' && filteredCommercants.length === 0 && commercants.length > 0 && (
                  <span className="text-sm text-gray-500">Aucun commerçant trouvé</span>
                )}
                {commercants.length === 0 && (
                  <span className="text-sm text-gray-500">Chargement des commerçants...</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="card">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' || selectedCommercant !== 'all'
                ? 'Aucun produit ne correspond aux filtres sélectionnés.'
                : 'Aucun produit disponible pour le moment.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => {
              const mainImageRaw = Array.isArray(item.images) && item.images.length > 0
                ? item.images[0]
                : item.image || (Array.isArray(item.photos) && item.photos.length > 0 ? item.photos[0] : null);
              const mainImage = mainImageRaw
                ? cloudinaryDeliveryUrl(mainImageRaw, { width: CLOUDINARY_ADMIN_WIDTH.card }) || mainImageRaw
                : null;
              const fallbackIcon = item.type === 'prestation' ? <Calendar className="h-12 w-12 text-gray-400" /> : <Package className="h-12 w-12 text-gray-400" />;
              const currentUser = getCurrentUser();
              const isCommercant = resolveMerchantOperationalScope(currentUser).scopedToMerchant;
              
              return (
                <div 
                  key={item.id} 
                  onClick={(e) => {
                    // Ouvrir le modal de modification au clic sur la carte (photo ou contenu)
                    // Les boutons d'action ont stopPropagation, donc ils ne déclencheront pas ce clic
                    if (item.type === 'produit') {
                      handleEdit(e, item);
                    }
                  }}
                  className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-primary-300 ${
                    !item.actif 
                      ? 'border-red-300 bg-red-50/30' 
                      : 'border-gray-200'
                  }`}
                >
                  {/* Image */}
                  <div className="relative">
                    {mainImage ? (
                      <img 
                        src={mainImage} 
                        alt={item.nom}
                        className="w-full h-48 object-cover rounded-t-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-48 ${mainImage ? 'hidden' : 'flex'} items-center justify-center bg-gray-100 rounded-t-lg`}
                    >
                      {fallbackIcon}
                    </div>
                    
                    {/* Badges */}
                    <div className="absolute top-2 right-2 flex flex-col gap-1">
                      {!item.actif ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Masqué
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Actif
                        </span>
                      )}
                      {item.type === 'prestation' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Calendar className="h-3 w-3 mr-1" />
                          Prestation
                        </span>
                      )}
                      {item.prixPromo && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Promo
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-4">
                    <div className="mb-2">
                      <h4 className="font-medium text-gray-900 text-lg mb-1">{item.nom}</h4>
                      {item.categorie && (
                        <div className="flex items-center space-x-1 mb-1">
                          <Tag className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{item.categorie}</span>
                        </div>
                      )}
                      {/* Commerçant (uniquement pour SUPER_ADMIN) */}
                      {!isCommercant && (
                        <div className="mt-1">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {item.commercant?.nom || getCommercantName(item.commercantId) || 'Sans commerçant'}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {item.description || 'Aucune description'}
                    </p>

                    {/* Infos */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Prix</span>
                        <div className="flex items-center space-x-2">
                          {item.prixPromo && (
                            <span className="text-sm text-gray-400 line-through">{(item.prix || 0).toFixed(2)}€</span>
                          )}
                          <span className={`text-lg font-bold ${item.prixPromo ? 'text-red-600' : 'text-green-600'}`}>
                            {(item.prixPromo || item.prix || 0).toFixed(2)}€
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Stock</span>
                        <span className={`font-medium ${
                          item.stock === -1 ? 'text-blue-600' :
                          item.stock < 5 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {item.type === 'produit'
                            ? (item.stock === -1 ? 'Illimité' : item.stock)
                            : (
                              <span className="inline-flex items-center gap-1">
                                <Users className="h-3.5 w-3.5" />
                                Max {item.maxParticipants || 1}
                              </span>
                            )}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={(e) => handleEdit(e, item)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleToggleActif(e, item)}
                        className={item.actif 
                          ? 'text-orange-600 hover:text-orange-900' 
                          : 'text-green-600 hover:text-green-900'}
                        title={item.actif ? 'Désactiver' : 'Activer'}
                      >
                        {item.actif ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, item)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Supprimer"
                        disabled={deletingId === item.id}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination - Affichée uniquement pour les commerçants */}
        {(() => {
          const isCommercant = resolveMerchantOperationalScope(getCurrentUser()).scopedToMerchant;
          
          if (isCommercant && totalPages > 1) {
            return (
              <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage de <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> à{' '}
                      <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> sur{' '}
                      <span className="font-medium">{totalItems}</span> produits
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })(        )}
      </div>

      {/* Modal d'édition/ajout de produit */}
      <ProduitModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedProduit(null);
        }}
        produit={selectedProduit}
        commercantId={selectedProduit?.commercantId || getCommercantIdForNewProduit()}
        initialCategories={categories}
        onSave={async (savedProduit) => {
          // Recharger les données
          await fetchData();
          // Fermer le modal
          setShowModal(false);
          setSelectedProduit(null);
        }}
      />
    </div>
  );
};

export default Produits;